import { useEffect, useMemo, useState } from "react";
import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
} from "@mui/material";

type EnumLike = { [key: string]: string | number };

interface IGenericEnumSelect {
    enumObject: EnumLike;
    label: string;
    value: string | number | null;   // allow both string and number
    name: string;
    onChange: (
        event: { target: { name: string; value: string | number | null } },
        selectedOption: any,
        name: string
    ) => void;
    required?: boolean;
    disabled?: boolean;
    variant?: "outlined" | "standard" | "filled";
    fullWidth?: boolean;
    excludedIds?: Array<string | number>;
}

function splitByCapital(text: string) {
    return text.replace(/([A-Z])/g, " $1").trim();
}

export default function EnumSelect(props: IGenericEnumSelect) {
    const {
        enumObject,
        label,
        value,
        name,
        onChange,
        required,
        disabled,
        variant = "outlined",
        fullWidth = true,
        excludedIds = [],
    } = props;

    const [item, setItem] = useState<string | number | null>(null);

    // build options from enum
    const enumOptions = useMemo(() => {
        if (!enumObject) return [];
        return Object.keys(enumObject)
            .filter((k) => isNaN(Number(k))) // ignore numeric reverse-mapping keys
            .map((key) => ({
                id: enumObject[key] as string | number,
                name: splitByCapital(key),
            }));
    }, [enumObject]);

    // filter out excluded IDs
    const filteredOptions = useMemo(() => {
        return enumOptions.filter(
            (opt) => !excludedIds.map(String).includes(String(opt.id))
        );
    }, [enumOptions, excludedIds]);

    // find current selected option
    const currentItem = useMemo(() => {
        return filteredOptions.find((opt) => opt.id === value) ?? null;
    }, [filteredOptions, value]);

    useEffect(() => {
        setItem(currentItem?.id ?? null);
    }, [currentItem]);

    const handleChange = (event: SelectChangeEvent<string | number>) => {
        const newValue = event.target.value as string | number;
        const selectedOption =
            filteredOptions.find((opt) => opt.id === newValue) ?? null;
        setItem(newValue);
        onChange({ target: { name, value: newValue } }, selectedOption, name);
    };

    return (
        <FormControl
            fullWidth={fullWidth}
            required={required}
            disabled={disabled}
            variant={variant}
        >
            <InputLabel id={`${name}-label`}>{label}</InputLabel>
            <Select
                labelId={`${name}-label`}
                label={label}
                name={name}
                value={item ?? ""}
                onChange={handleChange}
            >
                {filteredOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                        {opt.name}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}